import { DatePicker, Select } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { TFunction } from 'i18next';
import Image from 'next/image';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AiOutlineCheck, AiOutlineEnvironment, AiOutlineLeft, AiOutlineRight, AiOutlineTeam } from 'react-icons/ai';
import styled from 'styled-components';

import { getCurrencySymbol } from '@/utils/currency';
import { BookingCost, FreePeriods, Location, Unit } from '@core/booking/booking.types';

function cloudinaryUrl(url: string, width: number): string {
  if (!url || !url.includes('res.cloudinary.com')) return url;
  const transforms = `q_auto:best,f_auto,w_${width},c_fill,dpr_2.0`;
  return url.replace('/upload/', `/upload/${transforms}/`);
}

const StyledDatePicker = styled.div`
  .ant-picker {
    border-radius: 12px !important;
    padding: 8px 16px !important;
    font-size: 16px !important;
    width: 100%;
  }
  .ant-picker-input > input {
    text-align: center;
    font-weight: 600;
    font-size: 15px !important;
    color: #404d61 !important;
  }
`;

const StyledSelect = styled.div`
  .ant-select {
    width: 100%;
  }
  .ant-select-selector {
    border-radius: 12px !important;
    height: 44px !important;
    padding: 0 16px !important;
    display: flex;
    align-items: center;
  }
  .ant-select-selection-item {
    font-weight: 600;
    color: #404d61;
    font-size: 14px;
  }
  .ant-select-selection-placeholder {
    color: #9ca3af;
    font-size: 14px;
  }
  .ant-select-arrow {
    color: var(--main) !important;
  }
`;

interface AppliedDiscount {
  hours: number;
  percentage: number;
}

interface TimeSlotPickerProps {
  room: Unit | null;
  location: Location | null;
  selectedDate: Dayjs;
  setSelectedDate: (date: Dayjs) => void;
  freePeriods: FreePeriods[];
  roomIndex: number;
  has12Hours: boolean;
  bookingCost: BookingCost | null;
  selectedStartTime: number | null;
  selectedDuration: number | null;
  onSelectTimeSlot: (roomIndex: number, startMinutes: number, durationMinutes: number, has12Hours: boolean) => void;
  onDeleteBooking: () => void;
  appliedDiscount?: AppliedDiscount | null;
  onApplyDiscountCode?: (code: string) => void;
}

// Calendar constants
const SLOT_SIZE = 30;
const HOUR_HEIGHT = 80;
const HALF_HOUR_HEIGHT = HOUR_HEIGHT / 2;
const TIME_GUTTER = 56;

interface CalendarEvent {
  startMinutes: number;
  endMinutes: number;
  type: 'available' | 'booked' | 'selected' | 'past';
}

function formatMinutes(minutes: number, has12Hours: boolean): string {
  const time = dayjs().startOf('day').add(minutes, 'minute');
  return time.format(has12Hours ? 'h:mm A' : 'HH:mm');
}

function formatHourLabel(hour: number, has12Hours: boolean): string {
  const time = dayjs().startOf('day').hour(hour);
  return time.format(has12Hours ? 'h A' : 'HH:00');
}

function formatDuration(minutes: number, t: TFunction): string {
  if (minutes < 60) return t('common.minutesShort', { count: minutes });
  const hours = minutes / 60;
  return t('common.hoursShort', { count: hours % 1 === 0 ? hours : parseFloat(hours.toFixed(1)) });
}

const TimeSlotPicker: FC<TimeSlotPickerProps> = ({
  room,
  location,
  selectedDate,
  setSelectedDate,
  freePeriods,
  roomIndex,
  has12Hours,
  bookingCost,
  selectedStartTime,
  selectedDuration,
  onSelectTimeSlot,
  onDeleteBooking,
  appliedDiscount,
  onApplyDiscountCode,
}) => {
  const { t, i18n } = useTranslation();
  const minBooked = room ? parseFloat(room.min_booked) * 60 : 30;
  const currencySymbol = getCurrencySymbol(bookingCost?.currency, location);
  const [localStart, setLocalStart] = useState<number | null>(null);
  const [localEnd, setLocalEnd] = useState<number | null>(null);
  const [discountInput, setDiscountInput] = useState('');
  const [discountApplying, setDiscountApplying] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);

  const roomFreePeriods = useMemo(() => {
    return freePeriods?.[roomIndex] || {};
  }, [freePeriods, roomIndex]);

  // Compute the time range for the calendar
  const calendarRange = useMemo(() => {
    const allStarts = Object.keys(roomFreePeriods).map(Number);
    if (allStarts.length === 0) {
      return { startHour: 8, endHour: 18 };
    }
    const minStart = Math.min(...allStarts);
    const maxStart = Math.max(...allStarts);
    const lastEndTimes = roomFreePeriods[maxStart];
    const maxEnd =
      Array.isArray(lastEndTimes) && lastEndTimes.length > 0 ? lastEndTimes[lastEndTimes.length - 1] : maxStart + 60;

    const startHour = Math.floor(minStart / 60);
    const endHour = Math.min(Math.ceil(maxEnd / 60), 24);
    return { startHour, endHour };
  }, [roomFreePeriods]);

  const calendarHeight = (calendarRange.endHour - calendarRange.startHour) * HOUR_HEIGHT;

  // All available start times (filtered by min_booked and past)
  const availableStartOptions = useMemo(() => {
    const now = dayjs();
    const isToday = selectedDate.isSame(now, 'day');
    const currentMinutes = isToday ? now.hour() * 60 + now.minute() : 0;

    const options: { value: number; label: string }[] = [];
    Object.entries(roomFreePeriods)
      .map(([startStr, endTimes]) => ({ start: Number(startStr), endTimes }))
      .filter(({ start, endTimes }) => {
        if (!Array.isArray(endTimes) || endTimes.length === 0) return false;
        if (isToday && start < currentMinutes) return false;
        const maxEnd = endTimes[endTimes.length - 1];
        return maxEnd - start >= minBooked;
      })
      .sort((a, b) => a.start - b.start)
      .forEach(({ start }) => {
        options.push({
          value: start,
          label: formatMinutes(start, has12Hours),
        });
      });

    return options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomFreePeriods, selectedDate, minBooked, has12Hours, i18n.language]);

  // Available end times based on selected start
  const availableEndOptions = useMemo(() => {
    if (localStart === null) return [];
    const endTimes = roomFreePeriods[localStart];
    if (!Array.isArray(endTimes) || endTimes.length === 0) return [];
    return endTimes
      .filter((end) => end - localStart >= minBooked)
      .sort((a, b) => a - b)
      .map((end) => ({
        value: end,
        label: `${formatMinutes(end, has12Hours)}  (${formatDuration(end - localStart, t)})`,
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStart, roomFreePeriods, minBooked, has12Hours, t, i18n.language]);

  // Build set of all available start times (for calendar visual)
  const allAvailableStarts = useMemo(() => {
    const now = dayjs();
    const isToday = selectedDate.isSame(now, 'day');
    const currentMinutes = isToday ? now.hour() * 60 + now.minute() : 0;

    const set = new Set<number>();
    Object.entries(roomFreePeriods).forEach(([startStr, endTimes]) => {
      if (!Array.isArray(endTimes) || endTimes.length === 0) return;
      const start = Number(startStr);
      if (isToday && start < currentMinutes) return;
      set.add(start);
    });
    return set;
  }, [roomFreePeriods, selectedDate]);

  // Build calendar events — merge contiguous blocks of the same status
  const calendarEvents = useMemo(() => {
    const now = dayjs();
    const isToday = selectedDate.isSame(now, 'day');
    const currentMinutes = isToday ? now.hour() * 60 + now.minute() : 0;
    const { startHour, endHour } = calendarRange;

    type SlotStatus = 'available' | 'booked' | 'selected' | 'past';
    const slots: { start: number; status: SlotStatus }[] = [];

    for (let m = startHour * 60; m < endHour * 60; m += SLOT_SIZE) {
      let status: SlotStatus = 'booked';

      if (isToday && m < currentMinutes) {
        status = 'past';
      } else if (allAvailableStarts.has(m)) {
        status = 'available';
      }

      // Confirmed booking overrides
      if (
        selectedStartTime !== null &&
        selectedDuration !== null &&
        m >= selectedStartTime &&
        m < selectedStartTime + selectedDuration
      ) {
        status = 'selected';
      }

      slots.push({ start: m, status });
    }

    // Merge contiguous slots
    const events: CalendarEvent[] = [];
    let i = 0;
    while (i < slots.length) {
      const eventStart = slots[i].start;
      const eventType = slots[i].status;
      let j = i + 1;
      while (j < slots.length && slots[j].status === eventType) {
        j += 1;
      }
      const eventEnd = j < slots.length ? slots[j].start : endHour * 60;
      events.push({ startMinutes: eventStart, endMinutes: eventEnd, type: eventType });
      i = j;
    }

    return events;
  }, [calendarRange, allAvailableStarts, selectedStartTime, selectedDuration, selectedDate]);

  // Hours array for grid lines
  const hours = useMemo(() => {
    const arr: number[] = [];
    for (let h = calendarRange.startHour; h <= calendarRange.endHour; h += 1) {
      arr.push(h);
    }
    return arr;
  }, [calendarRange]);

  // Sync local state from confirmed booking props
  useEffect(() => {
    if (selectedStartTime !== null && selectedDuration !== null) {
      setLocalStart(selectedStartTime);
      setLocalEnd(selectedStartTime + selectedDuration);
    }
  }, [selectedStartTime, selectedDuration]);

  // Reset local state when date changes
  useEffect(() => {
    setLocalStart(null);
    setLocalEnd(null);
    if (selectedStartTime !== null) {
      onDeleteBooking();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const handleStartChange = useCallback(
    (value: number | undefined) => {
      if (value === undefined || value === null) {
        // Cleared
        setLocalStart(null);
        setLocalEnd(null);
        if (selectedStartTime !== null) {
          onDeleteBooking();
        }
        return;
      }
      setLocalStart(value);
      setLocalEnd(null);
      if (selectedStartTime !== null) {
        onDeleteBooking();
      }
    },
    [selectedStartTime, onDeleteBooking],
  );

  const handleEndChange = useCallback(
    (value: number | undefined) => {
      if (value === undefined || value === null || localStart === null) {
        setLocalEnd(null);
        if (selectedStartTime !== null) {
          onDeleteBooking();
        }
        return;
      }
      setLocalEnd(value);
      const durationMinutes = value - localStart;
      onSelectTimeSlot(roomIndex, localStart, durationMinutes, has12Hours);
    },
    [localStart, roomIndex, has12Hours, onSelectTimeSlot, selectedStartTime, onDeleteBooking],
  );

  // Click on empty (available) area of the calendar to quick-book
  const handleCalendarClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickY = e.clientY - rect.top;

      // Ignore clicks on the time gutter (which mirrors to the inline-end side in RTL)
      const clickX = e.clientX - rect.left;
      const isRTL = getComputedStyle(e.currentTarget).direction === 'rtl';
      if (isRTL ? clickX > rect.width - TIME_GUTTER : clickX < TIME_GUTTER) return;

      // Convert Y position to minutes, snap to nearest 30-min slot
      const clickedMinutes = calendarRange.startHour * 60 + (clickY / HOUR_HEIGHT) * 60;
      const snappedMinutes = Math.floor(clickedMinutes / SLOT_SIZE) * SLOT_SIZE;

      // Filter out past times
      const now = dayjs();
      const isToday = selectedDate.isSame(now, 'day');
      if (isToday && snappedMinutes < now.hour() * 60 + now.minute()) return;

      // Check if this slot has available end times
      const endTimes = roomFreePeriods[snappedMinutes];
      if (!Array.isArray(endTimes) || endTimes.length === 0) return;

      const validEnds = endTimes.filter((end) => end - snappedMinutes >= minBooked);
      if (validEnds.length === 0) return;

      // Prefer 1 hour, fallback to last available end time
      const preferredEnd = snappedMinutes + 60;
      const chosenEnd = validEnds.includes(preferredEnd) ? preferredEnd : validEnds[validEnds.length - 1];
      const duration = chosenEnd - snappedMinutes;

      // Update dropdown state and create booking
      setLocalStart(snappedMinutes);
      setLocalEnd(chosenEnd);
      onSelectTimeSlot(roomIndex, snappedMinutes, duration, has12Hours);
    },
    [calendarRange, roomFreePeriods, minBooked, selectedDate, roomIndex, has12Hours, onSelectTimeSlot],
  );

  const onPrevDay = useCallback(() => {
    const prev = selectedDate.subtract(1, 'day');
    if (prev.isBefore(dayjs(), 'day')) return;
    setSelectedDate(prev);
  }, [selectedDate, setSelectedDate]);

  const onNextDay = useCallback(() => {
    setSelectedDate(selectedDate.add(1, 'day'));
  }, [selectedDate, setSelectedDate]);

  // Auto-scroll calendar to first available or selected slot
  useEffect(() => {
    if (!calendarRef.current) return;
    const targetEvent =
      calendarEvents.find((e) => e.type === 'selected') || calendarEvents.find((e) => e.type === 'available');
    if (targetEvent) {
      const offsetPx = ((targetEvent.startMinutes - calendarRange.startHour * 60) / 60) * HOUR_HEIGHT;
      calendarRef.current.scrollTop = Math.max(0, offsetPx - 20);
    }
  }, [calendarEvents, calendarRange]);

  const imageUrl = room?.image_link ? cloudinaryUrl(room.image_link, 600) : '/placeholder.webp';

  const minutesToPx = (minutes: number) => {
    return ((minutes - calendarRange.startHour * 60) / 60) * HOUR_HEIGHT;
  };

  return (
    <div className="flex flex-col md:flex-row gap-6 p-2 md:p-0">
      {/* Left Panel — Room Info */}
      <div
        className="w-full md:w-72 flex-shrink-0 rounded-2xl p-5 flex flex-col"
        style={{ backgroundColor: '#F8F9FA', border: '1px solid #E5E7EB' }}
      >
        <div className="relative w-full overflow-hidden rounded-xl mb-4" style={{ height: 160 }}>
          <Image
            src={imageUrl}
            alt={room?.name || t('meetingRoom.room')}
            fill
            style={{ objectFit: 'cover' }}
            unoptimized
          />
        </div>

        <h3 className="text-lg font-bold m-0 mb-1" style={{ color: '#404D61' }}>
          {room?.name}
        </h3>

        <div className="flex items-center gap-1 mb-1" style={{ color: '#707070' }}>
          <AiOutlineEnvironment size={14} />
          <span className="text-sm">{location?.name}</span>
        </div>

        {location?.address && (
          <p className="text-xs m-0 mb-3 ps-5" style={{ color: '#999' }}>
            {location.address}
          </p>
        )}

        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg mb-4"
          style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}
        >
          <AiOutlineTeam size={16} style={{ color: 'var(--main)' }} />
          <span className="text-sm font-medium" style={{ color: '#404D61' }}>
            {t('landing.upToPeople', { count: Number(room?.max_people_number) })}
          </span>
        </div>

        {appliedDiscount && (
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-lg mb-4"
            style={{ backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0' }}
          >
            <span
              className="flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0"
              style={{ width: 22, height: 22, backgroundColor: '#DCFCE7', color: '#166534' }}
            >
              %
            </span>
            <span className="text-sm" style={{ color: '#166534' }}>
              <Trans
                i18nKey="meetingRoom.discountAppliedBanner"
                count={appliedDiscount.hours}
                values={{ percentage: appliedDiscount.percentage }}
                components={{ strong: <strong /> }}
              />
            </span>
          </div>
        )}

        {bookingCost && (
          <div className="mt-auto pt-4" style={{ borderTop: '1px solid #E5E7EB' }}>
            <div className="flex justify-between text-sm mb-1">
              <span style={{ color: '#707070' }}>{t('meetingRoom.summary.subtotal')}</span>
              <span
                className="font-medium"
                style={{ color: '#404D61' }}
                dangerouslySetInnerHTML={{ __html: `${currencySymbol}${bookingCost.subTotal.toFixed(2)}` }}
              />
            </div>
            {bookingCost.vat > 0 && (
              <div className="flex justify-between text-sm mb-1">
                <span style={{ color: '#707070' }}>{t('meetingRoom.summary.vat', { percent: bookingCost.vat })}</span>
                <span
                  className="font-medium"
                  style={{ color: '#707070' }}
                  dangerouslySetInnerHTML={{
                    __html: `${currencySymbol}${(bookingCost.total - bookingCost.subTotal).toFixed(2)}`,
                  }}
                />
              </div>
            )}

            {/* Discount Code */}
            {onApplyDiscountCode && selectedStartTime !== null && selectedDuration !== null && (
              <div className="mt-3 mb-3">
                <label className="block text-xs font-medium mb-1.5" style={{ color: '#707070' }}>
                  {t('meetingRoom.discount.label')}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={discountInput}
                    onChange={(e) => setDiscountInput(e.target.value)}
                    placeholder={t('payment.enterCode')}
                    className="flex-1 px-3 py-2 text-sm rounded-lg border outline-none"
                    style={{ borderColor: '#E5E7EB', color: '#404D61' }}
                  />
                  <button
                    type="button"
                    disabled={!discountInput.trim() || discountApplying}
                    onClick={() => {
                      setDiscountApplying(true);
                      onApplyDiscountCode(discountInput.trim());
                      setTimeout(() => setDiscountApplying(false), 500);
                    }}
                    className="px-4 py-2 rounded-lg text-sm font-medium border-none cursor-pointer text-white"
                    style={{
                      backgroundColor: discountInput.trim() && !discountApplying ? 'var(--main)' : '#D1D5DB',
                    }}
                  >
                    {discountApplying ? '...' : t('payment.applyCode')}
                  </button>
                </div>
                {discountInput.trim() && bookingCost.discountCode !== undefined && (
                  <p
                    className="text-xs mt-1.5 m-0 font-medium"
                    style={{ color: bookingCost.discountCode ? '#166534' : '#DC2626' }}
                  >
                    {bookingCost.discountCode
                      ? t('payment.discountApplied', { code: bookingCost.discountCode })
                      : t('payment.codeInvalid')}
                  </p>
                )}
              </div>
            )}

            <div
              className="flex justify-between text-base font-bold mt-2 pt-2"
              style={{ borderTop: '1px solid #E5E7EB' }}
            >
              <span style={{ color: '#404D61' }}>{t('meetingRoom.summary.total')}</span>
              <span
                style={{ color: 'var(--main)' }}
                dangerouslySetInnerHTML={{ __html: `${currencySymbol}${bookingCost.total.toFixed(2)}` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Right Panel — Dropdowns + Calendar */}
      <div className="flex-1 min-w-0">
        {/* Date Picker */}
        <div className="mb-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onPrevDay}
              className="flex items-center justify-center w-10 h-10 rounded-lg border-none cursor-pointer transition-colors"
              style={{ backgroundColor: '#F3F4F6', color: '#404D61' }}
            >
              <AiOutlineLeft size={16} className="rtl:rotate-180" />
            </button>
            <StyledDatePicker className="flex-1">
              <DatePicker
                value={selectedDate}
                format="dddd, MMMM DD, YYYY"
                suffixIcon={null}
                allowClear={false}
                bordered={false}
                onChange={(date) => date && setSelectedDate(date)}
                disabledDate={(current) => current && current.isBefore(dayjs(), 'day')}
                style={{ backgroundColor: '#F3F4F6', width: '100%' }}
              />
            </StyledDatePicker>
            <button
              type="button"
              onClick={onNextDay}
              className="flex items-center justify-center w-10 h-10 rounded-lg border-none cursor-pointer transition-colors"
              style={{ backgroundColor: '#F3F4F6', color: '#404D61' }}
            >
              <AiOutlineRight size={16} className="rtl:rotate-180" />
            </button>
          </div>
        </div>

        {/* Start / End Time Dropdowns */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#404D61' }}>
              {t('landing.calendar.startTime')}
            </label>
            <StyledSelect>
              <Select
                value={localStart ?? undefined}
                onChange={handleStartChange}
                placeholder={t('meetingRoom.selectStart')}
                options={availableStartOptions}
                allowClear
                showSearch={false}
                notFoundContent={<span className="text-sm text-gray-400">{t('meetingRoom.noAvailableTimes')}</span>}
              />
            </StyledSelect>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#404D61' }}>
              {t('landing.calendar.endTime')}
            </label>
            <StyledSelect>
              <Select
                value={localEnd ?? undefined}
                onChange={handleEndChange}
                placeholder={localStart !== null ? t('meetingRoom.selectEnd') : '\u2014'}
                options={availableEndOptions}
                disabled={localStart === null}
                allowClear
                showSearch={false}
                notFoundContent={<span className="text-sm text-gray-400">{t('meetingRoom.selectStartFirst')}</span>}
              />
            </StyledSelect>
          </div>
        </div>

        {/* Full Day Calendar */}
        <div
          ref={calendarRef}
          className="overflow-y-auto rounded-xl"
          style={{ maxHeight: 380, border: '1px solid #E5E7EB', backgroundColor: '#FAFAFA' }}
        >
          <div
            className="relative"
            style={{ height: calendarHeight, minWidth: 280, cursor: 'pointer' }}
            onClick={handleCalendarClick}
          >
            {/* Hour grid lines + labels */}
            {hours.map((hour) => {
              const topPx = (hour - calendarRange.startHour) * HOUR_HEIGHT;
              return (
                <div
                  key={hour}
                  className="absolute w-full flex items-start"
                  style={{ top: topPx, zIndex: 1, pointerEvents: 'none' }}
                >
                  <div
                    className="flex-shrink-0 text-end pe-2 -mt-2"
                    style={{ width: TIME_GUTTER, color: '#9CA3AF', fontSize: 11, fontWeight: 500 }}
                  >
                    {formatHourLabel(hour, has12Hours)}
                  </div>
                  <div className="flex-1" style={{ borderTop: '1px solid #E5E7EB' }} />
                </div>
              );
            })}

            {/* Half-hour grid lines */}
            {hours.slice(0, -1).map((hour) => {
              const topPx = (hour - calendarRange.startHour) * HOUR_HEIGHT + HALF_HOUR_HEIGHT;
              return (
                <div
                  key={`half-${hour}`}
                  className="absolute w-full flex items-start"
                  style={{ top: topPx, zIndex: 1, pointerEvents: 'none' }}
                >
                  <div style={{ width: TIME_GUTTER }} />
                  <div className="flex-1" style={{ borderTop: '1px dashed #F0F0F0' }} />
                </div>
              );
            })}

            {/* Calendar event blocks — booked (red) and selected (brand) */}
            {calendarEvents
              .filter((event) => event.type === 'booked' || event.type === 'past' || event.type === 'selected')
              .map((event) => {
                const topPx = minutesToPx(event.startMinutes) + 1;
                const heightPx = ((event.endMinutes - event.startMinutes) / 60) * HOUR_HEIGHT - 2;

                if (event.type === 'selected') {
                  return (
                    <div
                      key={event.startMinutes}
                      className="absolute rounded-lg flex flex-col justify-center px-3"
                      style={{
                        top: topPx,
                        insetInlineStart: TIME_GUTTER + 4,
                        insetInlineEnd: 8,
                        height: heightPx,
                        backgroundColor: 'var(--main)',
                        borderInlineStart: '3px solid var(--main)',
                        zIndex: 3,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center gap-1">
                        <AiOutlineCheck size={14} color="#FFFFFF" />
                        <span className="text-sm font-semibold text-white">{t('meetingRoom.yourBooking')}</span>
                      </div>
                      <span className="text-xs text-white mt-0.5" style={{ opacity: 0.9 }}>
                        {formatMinutes(event.startMinutes, has12Hours)}
                        {' \u2013 '}
                        {formatMinutes(event.endMinutes, has12Hours)}
                        {' \u00b7 '}
                        {formatDuration(event.endMinutes - event.startMinutes, t)}
                      </span>
                    </div>
                  );
                }

                // Booked / Past — red block
                return (
                  <div
                    key={event.startMinutes}
                    className="absolute rounded-lg flex items-start px-3 pt-2"
                    style={{
                      top: topPx,
                      insetInlineStart: TIME_GUTTER + 4,
                      insetInlineEnd: 8,
                      height: heightPx,
                      backgroundColor: '#FEE2E2',
                      borderInlineStart: '3px solid #FCA5A5',
                      zIndex: 2,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="text-xs font-medium" style={{ color: '#DC2626' }}>
                      {t('meetingRoom.unavailable')}
                    </span>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-5 mt-3 px-1">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5' }} />
            <span className="text-xs" style={{ color: '#707070' }}>
              {t('meetingRoom.unavailable')}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: 'var(--main)' }} />
            <span className="text-xs" style={{ color: '#707070' }}>
              {t('meetingRoom.yourBookingLegend')}
            </span>
          </div>
        </div>

        {/* Confirmed Booking Summary */}
        {selectedStartTime !== null && selectedDuration !== null && (
          <div
            className="flex flex-wrap items-center gap-3 mt-3 px-4 py-3 rounded-xl"
            style={{ backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0' }}
          >
            <AiOutlineCheck size={16} style={{ color: '#166534' }} />
            <span className="text-sm font-medium" style={{ color: '#166534' }}>
              {selectedDate.format('ddd, MMM D')}
              {' \u00b7 '}
              {formatMinutes(selectedStartTime, has12Hours)}
              {' \u2013 '}
              {formatMinutes(selectedStartTime + selectedDuration, has12Hours)}
              {' \u00b7 '}
              {formatDuration(selectedDuration, t)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeSlotPicker;
